/* Says once, at the top, that an English reader is looking at Czech.

   It watches the **body**, not the title. The navigational layer — titles,
   claims, the names of parts — is fully translated, so a page whose heading is
   English and whose content is Czech would otherwise say nothing at all, which
   is the case the mark exists for.

   The per-string mark stays, quietly, for a page where only some fields fell
   back. On a page where none of the content is translated it would stripe two
   hundred lines, and a warning repeated two hundred times is decoration.

   OWNED BY ITEM 10 OF docs/plan/00-prestavba-2026-08.md. */

import { useI18n } from "../lib/i18n";
import { isAbsent } from "../lib/types";
import type { Doc } from "../lib/types";

/* Does anything inside the blocks carry an English string? One is enough: the
   question is whether the body was translated at all, not how far. */
function anyEnglish(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(anyEnglish);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.cs === "string") return typeof record.en === "string" && record.en !== "";
    return Object.values(record).some(anyEnglish);
  }
  return false;
}

/* A lesson keeps almost nothing in `blocks` — its body is the ten finished
   materials. Reading only the blocks would leave an English reader on an
   untranslated lesson with no mark at all.

   What is gathered here is the body and nothing but: the material itself, the
   script of a recording, the prompt that makes it. The goal, the titles and the
   one-line claims are the navigational layer, which is translated in full — and
   counting them as body would silence the mark on every lesson. */
function bodies(doc: Doc): unknown[] {
  const out: unknown[] = [...doc.blocks, ...(doc.parts ?? []).flatMap((p) => p.blocks)];
  if (doc.type !== "lesson") return out;
  for (const item of doc.kit) {
    /* A row the lesson has decided against is a sentence of the navigational
       layer, not body. A row whose material is not made yet is body all the
       same: the prompt is what a reader of that row is given, and a lesson
       that is a goal and ten Czech prompts has to carry the mark. */
    if (isAbsent(item)) continue;
    if (item.ready?.as === "blocks") out.push(...item.ready.blocks);
    if (item.ready?.as === "audio") out.push(item.ready.script);
    out.push(item.prompt, ...(item.note ?? []));
  }
  return out;
}

export function Untranslated({ doc }: { doc: Doc }) {
  const { locale, ui } = useI18n();
  if (locale === "cs") return null;
  const blocks = bodies(doc);
  if (!blocks.length || anyEnglish(blocks)) return null;
  return <p className="cat-untranslated">{ui("untranslated")}</p>;
}
